import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  TextField,
  Divider,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Add,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { TimezoneSelect } from '../components/TimezoneSelect';

interface CsvUpload {
  id: number;
  filename: string;
  upload_timestamp: string;
  total_contacts: number;
  csv_rows_count: number;
  rejected_contacts: number;
}

interface CallConfiguration {
  id: number;
  name: string;
  model_id: string;
  api_token: string;
  interval_seconds: number;
  max_retries: number;
  start_hour: number;
  end_hour: number;
  timezone: string;
  is_active: boolean;
  is_running: boolean;
}

export const CallManagerPage: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [timezone, setTimezone] = useState('US/Eastern');
  const [addContactsDialogOpen, setAddContactsDialogOpen] = useState(false);
  const [csvUploads, setCsvUploads] = useState<CsvUpload[]>([]);
  const [selectedCsvUpload, setSelectedCsvUpload] = useState<CsvUpload | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Configuration state
  const [currentConfig, setCurrentConfig] = useState<CallConfiguration | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Form state
  const [modelId, setModelId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [intervalSeconds, setIntervalSeconds] = useState(60);
  const [maxRetries, setMaxRetries] = useState(1);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  
  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState({
    modelId: '',
    apiToken: '',
    intervalSeconds: 60,
    maxRetries: 1,
    startHour: 9,
    endHour: 17,
    timezone: 'US/Eastern'
  });

  // Load configurations on component mount
  useEffect(() => {
    fetchConfigurations();
  }, []);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return (
      modelId !== originalValues.modelId ||
      apiToken !== originalValues.apiToken ||
      intervalSeconds !== originalValues.intervalSeconds ||
      maxRetries !== originalValues.maxRetries ||
      startHour !== originalValues.startHour ||
      endHour !== originalValues.endHour ||
      timezone !== originalValues.timezone
    );
  };

  const handleStart = () => {
    setIsRunning(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleStop = () => {
    setIsRunning(false);
    setIsPaused(false);
  };

  const fetchCsvUploads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching CSV uploads with token:', token ? 'present' : 'missing');
      
      const response = await fetch('/api/v1/csv-uploads/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      console.log('CSV uploads response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('CSV uploads API error:', response.status, errorText);
        throw new Error(`Failed to fetch CSV uploads: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('CSV uploads data received:', data);
      
      // Handle paginated response
      const uploads = data.uploads || data;
      setCsvUploads(Array.isArray(uploads) ? uploads : []);
    } catch (error) {
      console.error('Error fetching CSV uploads:', error);
      setCsvUploads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContactsClick = () => {
    fetchCsvUploads();
    setAddContactsDialogOpen(true);
  };

  const handleAddContacts = () => {
    if (selectedCsvUpload) {
      // TODO: Implement adding contacts to call manager
      console.log('Adding contacts from CSV upload:', selectedCsvUpload);
      setAddContactsDialogOpen(false);
      setSelectedCsvUpload(null);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchConfigurations = async () => {
    setConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/calls/configurations', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch configurations');
      }
      const data = await response.json();
      
      // Load the first configuration if available
      if (data.length > 0) {
        const config = data[0];
        setCurrentConfig(config);
        setModelId(config.model_id || '');
        setApiToken(config.api_token || '');
        setIntervalSeconds(config.interval_seconds || 60);
        setMaxRetries(config.max_retries || 1);
        setStartHour(config.start_hour || 9);
        setEndHour(config.end_hour || 17);
        setTimezone(config.timezone || 'US/Eastern');
        setIsRunning(config.is_running || false);
        
        // Set original values for change detection
        setOriginalValues({
          modelId: config.model_id || '',
          apiToken: config.api_token || '',
          intervalSeconds: config.interval_seconds || 60,
          maxRetries: config.max_retries || 1,
          startHour: config.start_hour || 9,
          endHour: config.end_hour || 17,
          timezone: config.timezone || 'US/Eastern'
        });
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveConfiguration = async () => {
    setConfigLoading(true);
    
    try {
      // Show spinner for 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const token = localStorage.getItem('token');
      const configData = {
        name: 'Default Configuration',
        model_id: modelId,
        api_token: apiToken,
        interval_seconds: intervalSeconds,
        max_retries: maxRetries,
        start_hour: startHour,
        end_hour: endHour,
        timezone: timezone,
      };

      let response;
      if (currentConfig) {
        // Update existing configuration
        response = await fetch(`/api/v1/calls/configurations/${currentConfig.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(configData),
        });
      } else {
        // Create new configuration
        response = await fetch('/api/v1/calls/configurations', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(configData),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }
      
      // Update original values to reflect saved state
      setOriginalValues({
        modelId,
        apiToken,
        intervalSeconds,
        maxRetries,
        startHour,
        endHour,
        timezone
      });
      
      // Refresh configurations
      await fetchConfigurations();
    } catch (error) {
      console.error('Error saving configuration:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Call Manager
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Automated outbound calling system 
      </Typography>

      <Grid container spacing={3}>
        {/* Control Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Control Panel
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrow />}
                  onClick={handleStart}
                  disabled={isRunning}
                  fullWidth
                >
                  Start Calling
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Pause />}
                  onClick={handlePause}
                  disabled={!isRunning}
                  fullWidth
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={handleStop}
                  disabled={!isRunning}
                  fullWidth
                >
                  Stop
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Status: {isRunning ? (isPaused ? 'Paused' : 'Running') : 'Stopped'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model ID"
                    placeholder="Enter Synthflow Model ID"
                    variant="outlined"
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="API Token"
                    type={showApiToken ? "text" : "password"}
                    placeholder="Enter API Token"
                    variant="outlined"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowApiToken(!showApiToken)}
                            edge="end"
                          >
                            {showApiToken ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Call Interval (seconds)"
                    type="number"
                    value={intervalSeconds}
                    onChange={(e) => setIntervalSeconds(parseInt(e.target.value) || 60)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Max Retries"
                    type="number"
                    value={maxRetries}
                    onChange={(e) => setMaxRetries(parseInt(e.target.value) || 1)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Hour"
                    type="number"
                    value={startHour}
                    onChange={(e) => setStartHour(parseInt(e.target.value) || 9)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Hour"
                    type="number"
                    value={endHour}
                    onChange={(e) => setEndHour(parseInt(e.target.value) || 17)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TimezoneSelect
                    value={timezone}
                    onChange={setTimezone}
                    label="Timezone"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      variant="contained"
                      onClick={saveConfiguration}
                      disabled={configLoading || !hasUnsavedChanges()}
                    >
                      {configLoading ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Source Contacts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Source Contacts
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddContactsClick}
                >
                  Add Contacts
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No contacts added yet. Click "Add Contact" to get started.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Call Logs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Call Logs
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No call logs yet. Start calling to see logs here.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Contacts Dialog */}
      <Dialog open={addContactsDialogOpen} onClose={() => setAddContactsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Contacts from CSV Upload</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {!loading && (!csvUploads || csvUploads.length === 0) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No CSV Uploads Found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  You need to upload CSV files first. Go to the CSV Uploads page to upload your contact files.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    setAddContactsDialogOpen(false);
                    // Navigate to CSV uploads page
                    window.location.href = '/calls/csv-uploads';
                  }}
                >
                  Go to CSV Uploads
                </Button>
              </Box>
            ) : (
              <Autocomplete
                fullWidth
                options={csvUploads || []}
                getOptionLabel={(option) => `${option.id} --- ${option.filename}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCsvUpload}
                onChange={(_, newValue) => setSelectedCsvUpload(newValue)}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select CSV Upload"
                    variant="outlined"
                    helperText={`Choose a CSV file to add contacts from (${csvUploads?.length || 0} files available)`}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.id} --- {option.filename}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Uploaded: {formatTimestamp(option.upload_timestamp)}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        <Chip 
                          label={`${option.total_contacts} accepted`} 
                          size="small" 
                          color="success" 
                          variant="outlined" 
                        />
                        {option.rejected_contacts > 0 && (
                          <Chip 
                            label={`${option.rejected_contacts} rejected`} 
                            size="small" 
                            color="error" 
                            variant="outlined" 
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddContactsDialogOpen(false)}>
            Cancel
          </Button>
          {csvUploads && csvUploads.length > 0 && (
            <Button 
              onClick={handleAddContacts} 
              variant="contained" 
              disabled={!selectedCsvUpload}
            >
              Add Contacts
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
